import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import api from "@/services/api";
import { API_ENDPOINTS } from "@/config/api";
import { getOpenBetsForMarket, voidSingleOpenBet } from "@/services/api";
import SettlementModal from "@/components/SettlementModal";
import * as Dialog from "@radix-ui/react-dialog";

const LIMIT = 100;
const DEFAULT_OFFSET = 0;

export default function MatchSettlement() {
  const [activeTab, setActiveTab] = useState("MO");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [modalMode, setModalMode] = useState("declare");
  const [selectedMatch, setSelectedMatch] = useState(null);

  // Per-bet void state
  const [expandedRow, setExpandedRow] = useState(null);
  const [openBets, setOpenBets] = useState([]);
  const [betsLoading, setBetsLoading] = useState(false);
  const [voidConfirm, setVoidConfirm] = useState(null);

  const fetchMatches = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.MOMATCHES, {
        params: { limit: LIMIT, offset: DEFAULT_OFFSET },
      });
      if (response.data?.success && Array.isArray(response.data.data)) {
        setMatches(response.data.data);
      } else {
        setMatches([]);
      }
    } catch (error) {
      if (!silent) console.error("Error fetching MO/BM matches:", error);
      setMatches([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatches();
    const interval = setInterval(() => fetchMatches(true), 2000);
    return () => clearInterval(interval);
  }, []);

  const filteredMatches = matches.filter((m) => m.gameType === activeTab);

  const handleDeclare = (row) => {
    setSelectedMatch(row);
    setModalMode("declare");
    setModalShow(true);
  };

  const handleVoid = (row) => {
    setSelectedMatch(row);
    setModalMode("void");
    setModalShow(true);
  };

  const handleSettlementSubmit = async () => {
    setModalShow(false);
    setSelectedMatch(null);
    await fetchMatches(true);
  };

  // ── Per-bet expand ──
  const handleExpand = async (row) => {
    const key = `${row.matchId}-${row.marketType}-${row.gameType}`;
    if (expandedRow === key) {
      setExpandedRow(null);
      setOpenBets([]);
      return;
    }
    setExpandedRow(key);
    setBetsLoading(true);
    try {
      const resp = await getOpenBetsForMarket({
        match_id: row.matchId,
        market_type: row.marketType,
        game_type: row.gameType,
      });
      setOpenBets(resp?.success ? resp.data : []);
    } catch {
      setOpenBets([]);
    } finally {
      setBetsLoading(false);
    }
  };

  const handleVoidSingleBet = (bet) => {
    setVoidConfirm({ ...bet, loading: false });
  };

  const confirmVoidSingle = async () => {
    if (!voidConfirm) return;
    setVoidConfirm(prev => ({ ...prev, loading: true }));
    try {
      const resp = await voidSingleOpenBet({ bet_id: voidConfirm.id });
      if (resp?.success) {
        alert("Bet voided successfully for user: " + (voidConfirm.username || voidConfirm.userId));
      } else {
        alert("Failed: " + (resp?.error || "Unknown error"));
      }
    } catch (err) {
      alert("Error: " + (err?.response?.data?.error || err.message));
    } finally {
      setVoidConfirm(null);
      fetchMatches(true);
      if (expandedRow) {
        // Re-fetch the expanded bets
        const parts = expandedRow.split("-");
        // Can't easily re-parse, so just clear
        setExpandedRow(null);
        setOpenBets([]);
      }
    }
  };

  const isExpanded = (row) =>
    expandedRow === `${row.matchId}-${row.marketType}-${row.gameType}`;

  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 pt-4"
        >
          <div className="flex justify-between items-center mb-1">
            <div style={{ color: "#cae0e8", fontSize: "24px" }} className="font-semibold">
              Match Settlement
            </div>
            <button
              type="button"
              onClick={() => fetchMatches()}
              disabled={loading}
              className="px-4 py-2 bg-[#11374a] text-white text-[14px] font-bold hover:bg-[#1a4f66] transition-colors disabled:opacity-50"
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
          <div className="w-full h-px bg-white mb-2"></div>
        </div>

        {/* Tabs */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-8px" }}
          className="shadow-sm px-4 pt-4 pb-2"
        >
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => { setActiveTab("MO"); setExpandedRow(null); }}
              className={`px-4 py-2 text-[12px] font-bold transition-colors ${
                activeTab === "MO" ? "bg-[#333d79ff] text-white" : "bg-gray-600 text-white"
              }`}
            >
              Match Odds
            </button>
            <button
              onClick={() => { setActiveTab("BM"); setExpandedRow(null); }}
              className={`px-4 py-2 text-[12px] font-bold transition-colors ${
                activeTab === "BM" ? "bg-[#333d79ff] text-white" : "bg-gray-600 text-white"
              }`}
            >
              Bookmaker
            </button>
          </div>
        </div>

        {/* Table */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-8px", minHeight: "500px" }}
          className="shadow-sm p-4"
        >
          <div className="flex-1 min-w-0">
            <div className="overflow-hidden">
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2 text-white font-bold text-[15px] w-[30%]">Match Title</th>
                    <th className="text-left px-4 py-2 text-white font-bold text-[15px] w-[20%]">Market Type</th>
                    <th className="text-right px-4 py-2 text-white font-semibold w-[15%]">Total Bets</th>
                    <th className="text-center px-4 py-2 text-white font-bold text-[15px] w-[25%]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} className="text-center text-white py-4">Loading...</td></tr>
                  ) : filteredMatches.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-white py-4">No matches found</td></tr>
                  ) : (
                    filteredMatches.map((row, index) => (
                      <React.Fragment key={`${row.matchId}-${row.eventId}-${row.gameType}-${index}`}>
                        <tr className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""}`}>
                          <td className="py-2 px-4 text-[#2ce5dd] font-medium truncate">{row.matchTitle ?? `${row.teamOne} vs ${row.teamTwo}`}</td>
                          <td className="py-2 px-4 text-white font-bold truncate">{row.marketType || "-"}</td>
                          <td className="py-2 px-4 text-right text-white font-bold">{row.totalBets ?? 0}</td>
                          <td className="py-2 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleDeclare(row)}
                                className="px-3 py-1 bg-[#11374a] text-white text-[12px] font-bold hover:bg-[#1a4f66] transition-colors"
                              >
                                Declare
                              </button>
                              <button
                                onClick={() => handleVoid(row)}
                                className="px-3 py-1 bg-gray-600 text-white text-[12px] font-bold hover:bg-gray-500 transition-colors"
                              >
                                Void
                              </button>
                              <button
                                onClick={() => handleExpand(row)}
                                className="px-3 py-1 bg-[#c2410c] text-white text-[12px] font-bold hover:bg-[#ea580c] transition-colors"
                              >
                                {isExpanded(row) ? "Hide" : "Per Bet"}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded per-bet view */}
                        {isExpanded(row) && (
                          <tr>
                            <td colSpan={4} className="px-4 pb-3 pt-0">
                              <div className="bg-[#0d1025] border border-[#252d5c] p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-[#cae0e8] text-[14px] font-bold">
                                    Individual Bets &mdash; {row.marketType}
                                  </span>
                                  <span className="text-gray-500 text-[12px]">
                                    {openBets.length} bet{openBets.length !== 1 ? "s" : ""}
                                  </span>
                                </div>
                                {betsLoading ? (
                                  <div className="text-gray-400 text-center py-6 text-sm">Loading bets...</div>
                                ) : openBets.length === 0 ? (
                                  <div className="text-gray-400 text-center py-6 text-sm">No open bets found</div>
                                ) : (
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-[#252d5c]">
                                        <th className="text-left px-3 py-2 text-gray-400 font-semibold text-[12px]">Bet ID</th>
                                        <th className="text-left px-3 py-2 text-gray-400 font-semibold text-[12px]">User</th>
                                        <th className="text-left px-3 py-2 text-gray-400 font-semibold text-[12px]">Selection</th>
                                        <th className="text-center px-3 py-2 text-gray-400 font-semibold text-[12px]">Bet Type</th>
                                        <th className="text-right px-3 py-2 text-gray-400 font-semibold text-[12px]">Odds</th>
                                        <th className="text-right px-3 py-2 text-gray-400 font-semibold text-[12px]">Stake</th>
                                        <th className="text-right px-3 py-2 text-gray-400 font-semibold text-[12px]">Liability</th>
                                        <th className="text-center px-3 py-2 text-gray-400 font-semibold text-[12px]">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {openBets.map((bet, bIdx) => (
                                        <tr key={bet.id} className={`border-b border-[#1a1f3d] ${bIdx % 2 === 0 ? "bg-[rgba(255,255,255,0.02)]" : ""}`}>
                                          <td className="px-3 py-2.5 text-[#2ce5dd] font-medium">#{bet.id}</td>
                                          <td className="px-3 py-2.5 text-white font-medium">{bet.username}</td>
                                          <td className="px-3 py-2.5 text-white">{bet.selectionName || "-"}</td>
                                          <td className="px-3 py-2.5 text-center">
                                            <span className={`inline-block min-w-[40px] px-2 py-0.5 text-[11px] font-bold text-center text-white ${
                                              bet.betType === "back" || bet.betType === "yes" || bet.betType === "Yes"
                                                ? "bg-[#3b82f6]" : "bg-[#ec4899]"
                                            }`}>
                                              {(bet.betType || "-").toUpperCase()}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2.5 text-right text-white">{bet.odds ?? "-"}</td>
                                          <td className="px-3 py-2.5 text-right text-white">{bet.stakeAmount != null ? bet.stakeAmount.toFixed(2) : "-"}</td>
                                          <td className="px-3 py-2.5 text-right text-white">{bet.liability != null ? bet.liability.toFixed(2) : "-"}</td>
                                          <td className="px-3 py-2.5 text-center">
                                            <button
                                              onClick={() => handleVoidSingleBet(bet)}
                                              className="px-3 py-1.5 bg-[#c2410c] text-white text-[12px] font-bold hover:bg-[#ea580c] transition-colors"
                                            >
                                              Void Bet
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <SettlementModal
        show={modalShow}
        onHide={() => { setModalShow(false); setSelectedMatch(null); }}
        mode={modalMode}
        match={selectedMatch}
        onSubmit={handleSettlementSubmit}
      />

      {/* Void single bet confirmation */}
      <Dialog.Root open={!!voidConfirm} onOpenChange={(open) => { if (!open) setVoidConfirm(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60" style={{ zIndex: 9999 }} />
          <Dialog.Content
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#222951] border border-[#333d79] p-6 shadow-xl w-[calc(100vw-2rem)] max-w-[480px]"
            style={{ zIndex: 10000 }}
          >
            <Dialog.Title className="text-white text-lg font-bold mb-4">Void Single Bet</Dialog.Title>
            <div className="border-l-4 border-[#f59e0b] bg-[rgba(245,158,11,0.08)] p-4 mb-5">
              <div className="text-white text-sm leading-relaxed">
                <p>
                  Void bet <strong>#{voidConfirm?.id}</strong> for user{" "}
                  <span className="text-[#2ce5dd]">{voidConfirm?.username}</span>?
                </p>
                <p className="mt-2 text-gray-300">
                  Selection: <strong className="text-white">{voidConfirm?.selectionName}</strong> |
                  Stake: <strong className="text-white">{voidConfirm?.stakeAmount?.toFixed(2)}</strong> |
                  Odds: <strong className="text-white">{voidConfirm?.odds}</strong>
                </p>
                <p className="mt-2 text-gray-300">The stake will be refunded. This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Dialog.Close asChild>
                <button className="px-4 py-2 bg-gray-600 text-white text-sm font-bold hover:bg-gray-500 transition-colors">Cancel</button>
              </Dialog.Close>
              <button
                onClick={confirmVoidSingle}
                disabled={voidConfirm?.loading}
                className="px-4 py-2 bg-[#b91c1c] text-white text-sm font-bold hover:bg-[#dc2626] transition-colors disabled:opacity-50"
              >
                {voidConfirm?.loading ? "Processing..." : "Confirm Void"}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Layout>
  );
}
